import type { User } from "./api";

type PostHogModule = typeof import("posthog-js");
type PostHogClient = PostHogModule["default"];

let posthogClientPromise: Promise<PostHogClient | null> | null = null;
let identifiedUserId: string | null = null;
let lastPersonPropertiesKey: string | null = null;
let lastPageviewKey: string | null = null;

export function syncAuthenticatedPostHogUser(user: User): void {
  if (!posthogToken()) return;
  const alreadyIdentified = identifiedUserId === user.user_id;
  const personProperties = {
    has_email: Boolean(user.email),
    telegram_linked: Boolean(user.telegram),
  };
  const personPropertiesKey = JSON.stringify(personProperties);
  identifiedUserId = user.user_id;
  void posthogClient().then((posthog) => {
    if (!posthog) return;
    if (!alreadyIdentified) {
      posthog.identify(user.user_id, personProperties);
      lastPersonPropertiesKey = personPropertiesKey;
    } else if (lastPersonPropertiesKey !== personPropertiesKey) {
      posthog.setPersonProperties(personProperties);
      lastPersonPropertiesKey = personPropertiesKey;
    }
    posthog.startSessionRecording();
  });
}

export function resetPostHogUser(): void {
  if (!posthogClientPromise) return;
  void posthogClientPromise.then((posthog) => {
    if (!posthog) return;
    posthog.stopSessionRecording();
    posthog.reset();
    identifiedUserId = null;
    lastPersonPropertiesKey = null;
    lastPageviewKey = null;
  });
}

export function captureAuthenticatedPageview(routeName: string): void {
  if (!posthogToken() || !identifiedUserId) return;
  const key = `${identifiedUserId}:${window.location.pathname}${window.location.search}:${routeName}`;
  if (key === lastPageviewKey) return;
  lastPageviewKey = key;
  void posthogClient().then((posthog) => {
    if (!posthog || !identifiedUserId) return;
    const sanitizedUrl = sanitizedCurrentUrl();
    posthog.capture("$pageview", {
      $current_url: sanitizedUrl,
      $pathname: sanitizedPathname(window.location.pathname),
      chatcode_route: routeName,
    });
  });
}

async function posthogClient(): Promise<PostHogClient | null> {
  const token = posthogToken();
  if (!token || typeof window === "undefined") return null;
  if (posthogClientPromise) return posthogClientPromise;
  posthogClientPromise = import("posthog-js").then((mod) => {
    const posthog = mod.default;
    posthog.init(token, {
      api_host: posthogHost(),
      ui_host: posthogUiHost(),
      defaults: "2026-01-30",
      person_profiles: "identified_only",
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      disable_surveys: true,
      disable_web_experiments: true,
      enable_recording_console_log: false,
      mask_all_text: true,
      mask_all_element_attributes: true,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: "*",
        blockSelector: ".xterm, [data-posthog-block]",
      },
      before_send: (event) => {
        if (!event?.properties) return event;
        event.properties.$current_url = sanitizedCurrentUrl();
        event.properties.$pathname = sanitizedPathname(window.location.pathname);
        return event;
      },
    });
    return posthog;
  }).catch(() => null);
  return posthogClientPromise;
}

function posthogToken(): string {
  return envString("VITE_POSTHOG_PROJECT_TOKEN", "");
}

function posthogHost(): string {
  return envString("VITE_POSTHOG_HOST", "https://t.chatcode.dev");
}

function posthogUiHost(): string {
  return envString("VITE_POSTHOG_UI_HOST", "https://eu.posthog.com");
}

function envString(key: string, fallback: string): string {
  const viteEnv = import.meta.env[key];
  if (typeof viteEnv === "string") return viteEnv.trim();
  const processEnv = (globalThis as {
    process?: { env?: Record<string, string | undefined> };
  }).process?.env?.[key];
  if (typeof processEnv === "string") return processEnv.trim();
  return fallback;
}

function sanitizedCurrentUrl(): string {
  const url = new URL(window.location.href);
  url.pathname = sanitizedPathname(url.pathname);
  url.search = "";
  url.hash = "";
  return url.toString();
}

function sanitizedPathname(pathname: string): string {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  if (/^\/tg\/session\/[^/]+$/.test(normalized)) return "/tg/session/:sessionId";
  if (/^\/tg\/server\/[^/]+$/.test(normalized)) return "/tg/server/:vpsId";
  if (/^\/vps-[^/]+\/ses-[^/]+$/.test(normalized)) return "/:vpsId/:sessionId";
  if (/^\/vps-[^/]+\/settings$/.test(normalized)) return "/:vpsId/settings";
  if (/^\/vps-[^/]+$/.test(normalized)) return "/:vpsId";
  return normalized;
}
